import { useEffect, useState } from "react";

import { Input, type InputProps } from "@/lib/components/Input";

export interface NumberInputProps extends Omit<
  InputProps,
  "value" | "onChange" | "type"
> {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
}

// A numeric text input that clamps to an optional min/max. It renders a plain
// text field (not type="number") so there are no spinner arrows, while still
// only allowing whole numbers.
export function NumberInput({
  value,
  onValueChange,
  min,
  max,
  onBlur,
  ...props
}: NumberInputProps) {
  const [text, setText] = useState(String(value));

  // Keep the displayed text in sync when the value changes from outside.
  useEffect(() => {
    setText(String(value));
  }, [value]);

  const clamp = (n: number) => {
    let next = n;
    if (max !== undefined) next = Math.min(max, next);
    if (min !== undefined) next = Math.max(min, next);
    return next;
  };

  return (
    <Input
      type="text"
      inputMode="numeric"
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        // Allow only whole numbers (or an empty field while editing).
        if (raw !== "" && !/^\d+$/.test(raw)) return;
        if (raw === "") {
          setText("");
          return;
        }

        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) return;

        // Clamp the upper bound as you type so an over-max value can't be shown.
        const capped = max !== undefined ? Math.min(max, parsed) : parsed;
        setText(String(capped));
        onValueChange(clamp(capped));
      }}
      onBlur={(e) => {
        // Normalise the display on blur (empty or below-min becomes valid).
        const parsed = text === "" ? NaN : Number(text);
        const next = clamp(Number.isFinite(parsed) ? parsed : (min ?? 0));
        setText(String(next));
        onValueChange(next);
        onBlur?.(e);
      }}
      {...props}
    />
  );
}
